import {
  BooleanInput,
  FormDataConsumer,
  maxLength,
  ReferenceInput,
  required,
  SelectInput,
  SimpleForm,
  TextInput,
} from "react-admin";
import { Grid, useMediaQuery } from "@mui/material";
import { settings } from "app";
import { SectionTitle } from "features/SectionTitle";
import { erasmusRequests } from "api/erasmusApi";
import CustomDateInput from "server/components/Fields/CustomDateInput";
import ProfessorInput from "server/components/Fields/ProfessorInput";
import { MobilityVisitType } from "utils/constants";
import DocumentFileInput from "components/Erasmus/Partials/DocumentFileInput";
import TravelDocumentsInput from "components/Erasmus/Partials/TravelDocumentsInput";

const SaveForm = ({ onDocumentUpload, travelDocumentIds, onTravelDocumentsChange }) => {
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("md"));

  return (
    <SimpleForm>
      <Grid
        container
        spacing={4}
        sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
      >
        <Grid
          item
          xs={12}
        >
          <SectionTitle
            title="Данни за кандидата"
            paddingY={0}
          />
        </Grid>

        <Grid
          item
          xs={12}
          md={4}
        >
          <ProfessorInput
            source="employeeId"
            label="Служител"
            validate={required()}
            fullWidth
          />
        </Grid>

        <Grid
          item
          xs={12}
          md={4}
        >
          <TextInput
            source="universityName"
            label="Университет"
            validate={[required(), maxLength(100)]}
            fullWidth
          />
        </Grid>

        <Grid
          item
          xs={12}
          md={4}
        >
          <ReferenceInput
            source="mobilityVisitType"
            reference={erasmusRequests.getMobilityTypesSelect()}
          >
            <SelectInput
              validate={required()}
              label="Тип визита"
              optionText="nameBg"
            />
          </ReferenceInput>
        </Grid>

        <FormDataConsumer>
          {({ formData }) => {
            // The date applies to all visit types (BE accepts it for every type);
            // it is mandatory only for BIP. bipName/bipNumber are specific to BIP only.
            const isBip = formData.mobilityVisitType === MobilityVisitType.BIP;

            return (
              <>
                <Grid
                  item
                  xs={12}
                  md={6}
                  xl={4}
                >
                  <CustomDateInput
                    source="startDate"
                    label="Начална дата"
                    validate={isBip ? required("Задължително") : []}
                    required={isBip}
                  />
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={6}
                  xl={4}
                >
                  <CustomDateInput
                    source="endDate"
                    label="Крайна дата"
                    validate={isBip ? required("Задължително") : []}
                    required={isBip}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={6}
                  xl={2}
                >
                  <BooleanInput
                    source="isGreenTransport"
                    label="Зелен транспорт"
                  />
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={6}
                  xl={2}
                >
                  <BooleanInput
                    source="isSharedTransport"
                    label="Споделен транспорт"
                  />
                </Grid>
                {isBip && (
                  <Grid
                    container
                    spacing={4}
                    sx={{ margin: "0.2rem" }}
                  >
                    <Grid
                      item
                      xs={12}
                      md={6}
                      xl={4}
                    >
                      <TextInput
                        source="bipName"
                        label="BIP имe"
                        validate={required("Задължително")}
                      />
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      md={6}
                      xl={4}
                    >
                      <TextInput
                        source="bipNumber"
                        label="BIP номер"
                        validate={required("Задължително")}
                      />
                    </Grid>
                  </Grid>
                )}
              </>
            );
          }}
        </FormDataConsumer>
      </Grid>

      <Grid
        container
        spacing={4}
        sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
      >
        <Grid
          item
          xs={12}
        >
          <SectionTitle
            title="Документи"
            paddingY={0}
          />
        </Grid>

        <Grid
          item
          xs={12}
        >
          <DocumentFileInput
            label="Споразумение за мобилност"
            source="mobilityAgreementId"
            onUploadSuccess={id => onDocumentUpload("mobilityAgreementId", id)}
          />
        </Grid>

        <Grid
          item
          xs={12}
        >
          <DocumentFileInput
            label="CV"
            source="cvId"
            onUploadSuccess={id => onDocumentUpload("cvId", id)}
          />
        </Grid>

        <Grid
          item
          xs={12}
        >
          <DocumentFileInput
            label="Протокол за подбор"
            source="selectionProtocolId"
            onUploadSuccess={id => onDocumentUpload("selectionProtocolId", id)}
          />
        </Grid>

        <Grid
          item
          xs={12}
        >
          <DocumentFileInput
            label="Споразумение за финансиране"
            source="fundingAgreementId"
            onUploadSuccess={id => onDocumentUpload("fundingAgreementId", id)}
          />
        </Grid>

        <Grid
          item
          xs={12}
        >
          <DocumentFileInput
            label="Писмо за приемане"
            source="acceptanceLetterId"
            onUploadSuccess={id => onDocumentUpload("acceptanceLetterId", id)}
          />
        </Grid>

        <Grid
          item
          xs={12}
        >
          <DocumentFileInput
            label="Удостоверение за престой"
            source="certificateOfStayId"
            onUploadSuccess={id => onDocumentUpload("certificateOfStayId", id)}
          />
        </Grid>

        <FormDataConsumer>
          {({ formData }) =>
            formData.mobilityVisitType !== MobilityVisitType.Internship && (
              <Grid
                item
                xs={12}
              >
                <DocumentFileInput
                  label="Удостоверение за преподаване"
                  source="certificateOfTeachingId"
                  onUploadSuccess={id => onDocumentUpload("certificateOfTeachingId", id)}
                />
              </Grid>
            )
          }
        </FormDataConsumer>

        <Grid
          item
          xs={12}
        >
          <DocumentFileInput
            label="Доклад от командировка"
            source="businessTripReportId"
            onUploadSuccess={id => onDocumentUpload("businessTripReportId", id)}
          />
        </Grid>

        <Grid
          item
          xs={12}
        >
          <DocumentFileInput
            label="Заповед за командировка"
            source="businessTripOrderId"
            onUploadSuccess={id => onDocumentUpload("businessTripOrderId", id)}
          />
        </Grid>

        <Grid
          item
          xs={12}
        >
          <DocumentFileInput
            label="Медицинска застраховка"
            source="medicalInsuranceId"
            onUploadSuccess={id => onDocumentUpload("medicalInsuranceId", id)}
          />
        </Grid>

        <Grid
          item
          xs={12}
        >
          <DocumentFileInput
            label="Доклад на пътуващия"
            source="travellerReportId"
            onUploadSuccess={id => onDocumentUpload("travellerReportId", id)}
          />
        </Grid>

        <Grid
          item
          xs={12}
        >
          <SectionTitle
            title="Документи за пътуване"
            paddingY={0}
          />
        </Grid>

        <Grid
          item
          xs={12}
        >
          <TravelDocumentsInput
            value={travelDocumentIds}
            onChange={onTravelDocumentsChange}
          />
        </Grid>
      </Grid>
    </SimpleForm>
  );
};

export default SaveForm;
